import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Chip,
    Stack,
    Divider,
    Alert,
    Snackbar,
    LinearProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Close as CloseIcon,
    Email as EmailIcon,
    MoreVert as MoreVertIcon,
    ExpandMore as ExpandMoreIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, gql } from '@apollo/client';
import EmailTemplateManager from './EmailTemplateSetup';
import MainCard from 'ui-component/cards/MainCard';
import { useParams } from 'react-router-dom';

const GET_ROLES = gql`
    query GetRoles {
        event {
            roles
        }
    }
`;

const GET_ALL_EMAIL_TEMPLATES = gql`
    query GetAllEmailTemplates($eventId: String!, $role: ROLE!) {
        getAllEmailTemplates(eventId: $eventId, role: $role) {
            fromEmails {
                id
                role
                templateType
                subject
                body
                createdAt
                modified
            }
            fromTemplates {
                id
                role
                userId
                invitation_subject
                invitation_body
                invitation_is_html
                confirmation_subject
                confirmation_body
                confirmation_is_html
                accept_subject
                accept_body
                accept_is_html
                reject_subject
                reject_body
                reject_is_html
                created_at
                updated_at
            }
        }
    }
`;

const DELETE_EMAIL_TEMPLATE = gql`
    mutation DeleteEmailTemplate($eventId: String!, $templateId: Int!) {
        deleteEmailTemplate(eventId: $eventId, templateId: $templateId)
    }
`;

// Convert role to match ROLE enum format
const convertRoleToEnum = roleString => {
    if (!roleString) return roleString;
    let role = roleString.toLowerCase();
    const roleMap = {
        corporates: 'Corporates',
        ctf: 'CTF',
        crisissimulation: 'CrisisSimulation',
        hackathon: 'Hackathon',
        daypass: 'DayPass',
        certificate: 'Certificate',
        student: 'Student',
        meeting: 'Meeting',
        vendors: 'Vendors',
        board_members: 'Board_Members',
        oshrm_board_members: 'OSHRM_Board_Members',
        nama_delegate: 'nama_delegate',
    };
    return roleMap[role] || role;
};

export default function UnifiedEmailTemplateManager() {
    const { eventId } = useParams();
    const [selectedRole, setSelectedRole] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateType, setTemplateType] = useState('invitation');
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuTemplate, setMenuTemplate] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const { data: rolesData, loading: rolesLoading } = useQuery(GET_ROLES);

    const roles = rolesData?.event?.roles?.split(',')?.map(r => r.trim()) || [];

    const getTemplateTypeLabel = type => {
        const labels = {
            sender: 'Sender',
            accept: 'Accept',
            reject: 'Reject',
            upgrade: 'Upgrade',
            invitation: 'Invitation',
            confirmation: 'Confirmation',
        };
        return labels[type] || type;
    };

    const handleCreateTemplate = type => {
        setTemplateType(type);
        setSelectedTemplate(null);
        setCreateDialogOpen(true);
    };

    const handleEditTemplate = (template, type) => {
        setSelectedTemplate(template);
        setTemplateType(type);
        setEditDialogOpen(true);
    };

    const handleDeleteTemplate = templateId => {
        deleteTemplate({
            variables: {
                eventId,
                templateId: parseInt(templateId),
            },
        });
        handleMenuClose();
    };

    const handleMenuOpen = (event, template) => {
        setAnchorEl(event.currentTarget);
        setMenuTemplate(template);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuTemplate(null);
    };

    const handlePreview = template => {
        setPreviewTemplate(template);
        setPreviewDialogOpen(true);
    };

    const [deleteTemplate] = useMutation(DELETE_EMAIL_TEMPLATE, {
        onCompleted: () => {
            setSnackbar({ open: true, message: 'Template deleted successfully', severity: 'success' });
        },
        onError: error => {
            setSnackbar({ open: true, message: error.message, severity: 'error' });
        },
    });

    return (
        <MainCard title='Email Templates'>
            <Box sx={{ mb: 4, mt: 2 }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                    Manage all email templates across all roles. Create, edit, preview, and delete templates.
                </Typography>
                <Stack direction='row' spacing={2} flexWrap='wrap' sx={{ gap: 2 }}>
                    <Button
                        variant='contained'
                        startIcon={<AddIcon />}
                        onClick={() => handleCreateTemplate('invitation')}
                        sx={{ minWidth: 200 }}
                    >
                        Create Invitation Template
                    </Button>
                    <Button
                        variant='outlined'
                        startIcon={<AddIcon />}
                        onClick={() => handleCreateTemplate('confirmation')}
                        sx={{ minWidth: 200 }}
                    >
                        Create Confirmation Template
                    </Button>
                    <Button
                        variant='outlined'
                        startIcon={<AddIcon />}
                        onClick={() => handleCreateTemplate('accept')}
                        sx={{ minWidth: 200 }}
                    >
                        Create Accept Template
                    </Button>
                    <Button
                        variant='outlined'
                        startIcon={<AddIcon />}
                        onClick={() => handleCreateTemplate('reject')}
                        sx={{ minWidth: 200 }}
                    >
                        Create Reject Template
                    </Button>
                </Stack>
            </Box>

            <Divider sx={{ my: 4 }} />

            {rolesLoading ? (
                <LinearProgress />
            ) : (
                <Box>
                    {roles.map(role => {
                        const enumRole = convertRoleToEnum(role);
                        return (
                            <RoleTemplateSection
                                key={role}
                                eventId={eventId}
                                role={enumRole}
                                roleLabel={role}
                                onEdit={handleEditTemplate}
                                onDelete={handleDeleteTemplate}
                                onPreview={handlePreview}
                                onMenuOpen={handleMenuOpen}
                            />
                        );
                    })}
                </Box>
            )}

            {/* Preview Dialog */}
            <Dialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                maxWidth='md'
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant='h6'>Template Preview</Typography>
                        <IconButton onClick={() => setPreviewDialogOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {previewTemplate && (
                        <Box>
                            <Typography variant='subtitle1' gutterBottom>
                                <strong>Subject:</strong> {previewTemplate.subject || 'No Subject'}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Box
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: 'grey.50',
                                    minHeight: 200,
                                }}
                                dangerouslySetInnerHTML={{ __html: previewTemplate.body || '' }}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Create/Edit Dialog */}
            <Dialog
                open={createDialogOpen || editDialogOpen}
                onClose={() => {
                    setCreateDialogOpen(false);
                    setEditDialogOpen(false);
                    setSelectedTemplate(null);
                }}
                maxWidth='md'
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant='h6'>
                            {editDialogOpen ? 'Edit' : 'Create'} {getTemplateTypeLabel(templateType)} Template
                        </Typography>
                        <IconButton
                            onClick={() => {
                                setCreateDialogOpen(false);
                                setEditDialogOpen(false);
                                setSelectedTemplate(null);
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 3, minHeight: 400 }}>
                    <EmailTemplateManager
                        contextId={eventId}
                        templateType={templateType}
                        eventId={eventId}
                        role={selectedTemplate?.role || roles[0] ? convertRoleToEnum(roles[0]) : ''}
                        onSave={() => {
                            setCreateDialogOpen(false);
                            setEditDialogOpen(false);
                            setSelectedTemplate(null);
                            window.location.reload(); // Refresh to show new templates
                        }}
                        initialTemplate={selectedTemplate}
                        initialSubject={
                            selectedTemplate?.subject ||
                            selectedTemplate?.invitation_subject ||
                            selectedTemplate?.confirmation_subject ||
                            selectedTemplate?.accept_subject ||
                            selectedTemplate?.reject_subject ||
                            ''
                        }
                        initialBody={
                            selectedTemplate?.body ||
                            selectedTemplate?.invitation_body ||
                            selectedTemplate?.confirmation_body ||
                            selectedTemplate?.accept_body ||
                            selectedTemplate?.reject_body ||
                            ''
                        }
                        initialIsHtml={
                            selectedTemplate?.isHtml !== undefined
                                ? selectedTemplate.isHtml
                                : selectedTemplate?.invitation_is_html !== undefined
                                ? selectedTemplate.invitation_is_html
                                : true
                        }
                    />
                </DialogContent>
            </Dialog>

            {/* Context Menu */}
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                {menuTemplate && menuTemplate.templateType && (
                    <MenuItem
                        onClick={() => {
                            handleEditTemplate(menuTemplate, menuTemplate.templateType);
                            handleMenuClose();
                        }}
                    >
                        <ListItemIcon>
                            <EditIcon fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                )}
                {menuTemplate && menuTemplate.id && typeof menuTemplate.id === 'number' && (
                    <MenuItem
                        onClick={() => handleDeleteTemplate(menuTemplate.id)}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon>
                            <DeleteIcon fontSize='small' color='error' />
                        </ListItemIcon>
                        <ListItemText>Delete</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </MainCard>
    );
}

function RoleTemplateSection({ eventId, role, roleLabel, onEdit, onDelete, onPreview, onMenuOpen }) {
    const { data, loading, error, refetch } = useQuery(GET_ALL_EMAIL_TEMPLATES, {
        variables: { eventId, role },
        skip: !eventId || !role,
    });

    const [expanded, setExpanded] = React.useState(true);

    const fromEmails = data?.getAllEmailTemplates?.fromEmails || [];
    const fromTemplates = data?.getAllEmailTemplates?.fromTemplates || [];

    const getTemplateTypeLabel = type => {
        const labels = {
            sender: 'Sender',
            accept: 'Accept',
            reject: 'Reject',
            upgrade: 'Upgrade',
            invitation: 'Invitation',
            confirmation: 'Confirmation',
        };
        return labels[type] || type;
    };

    if (loading) {
        return (
            <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant='h6'>{roleLabel} Templates</Typography>
                    <LinearProgress sx={{ width: '100%', ml: 2 }} />
                </AccordionSummary>
            </Accordion>
        );
    }

    if (error) {
        return (
            <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant='h6'>{roleLabel} Templates</Typography>
                    <Alert severity='error' sx={{ ml: 2 }}>
                        {error.message}
                    </Alert>
                </AccordionSummary>
            </Accordion>
        );
    }

    return (
        <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant='h6'>{roleLabel} Templates</Typography>
                <Chip
                    label={`${fromEmails.length + fromTemplates.length} templates`}
                    size='small'
                    sx={{ ml: 2 }}
                />
            </AccordionSummary>
            <AccordionDetails>
                {fromEmails.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                            Email Configuration Templates
                        </Typography>
                        <Grid container spacing={2}>
                            {fromEmails.map((template, index) => (
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    lg={4}
                                    key={`email-${template.id}-${template.templateType}-${index}`}
                                >
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 4,
                                            },
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'start',
                                                    mb: 2,
                                                }}
                                            >
                                                <Chip
                                                    label={getTemplateTypeLabel(template.templateType)}
                                                    color='primary'
                                                    size='small'
                                                />
                                                <IconButton
                                                    size='small'
                                                    onClick={e => onMenuOpen(e, { ...template, templateType: template.templateType })}
                                                >
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </Box>
                                            <Typography variant='h6' noWrap gutterBottom>
                                                {template.subject || 'No Subject'}
                                            </Typography>
                                            <Typography
                                                variant='body2'
                                                color='text.secondary'
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    mb: 2,
                                                }}
                                            >
                                                {template.body?.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                            </Typography>
                                            <Stack direction='row' spacing={1}>
                                                <Button
                                                    size='small'
                                                    variant='outlined'
                                                    startIcon={<VisibilityIcon />}
                                                    onClick={() => onPreview(template)}
                                                    fullWidth
                                                >
                                                    Preview
                                                </Button>
                                                <Button
                                                    size='small'
                                                    variant='outlined'
                                                    startIcon={<EditIcon />}
                                                    onClick={() => onEdit(template, template.templateType)}
                                                    fullWidth
                                                >
                                                    Edit
                                                </Button>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

                {fromTemplates.length > 0 && (
                    <Box>
                        <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                            Custom Templates
                        </Typography>
                        <Grid container spacing={2}>
                            {fromTemplates.map((template, templateIndex) => {
                                const templateTypes = [
                                    {
                                        type: 'invitation',
                                        subject: template.invitation_subject,
                                        body: template.invitation_body,
                                        isHtml: template.invitation_is_html,
                                    },
                                    {
                                        type: 'confirmation',
                                        subject: template.confirmation_subject,
                                        body: template.confirmation_body,
                                        isHtml: template.confirmation_is_html,
                                    },
                                    {
                                        type: 'accept',
                                        subject: template.accept_subject,
                                        body: template.accept_body,
                                        isHtml: template.accept_is_html,
                                    },
                                    {
                                        type: 'reject',
                                        subject: template.reject_subject,
                                        body: template.reject_body,
                                        isHtml: template.reject_is_html,
                                    },
                                ].filter(t => t.subject && t.body);

                                return templateTypes.map((t, typeIndex) => (
                                    <Grid
                                        item
                                        xs={12}
                                        md={6}
                                        lg={4}
                                        key={`template-${template.id}-${t.type}-${templateIndex}-${typeIndex}`}
                                    >
                                        <Card
                                            sx={{
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    transform: 'translateY(-4px)',
                                                    boxShadow: 4,
                                                },
                                            }}
                                        >
                                            <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'start',
                                                        mb: 2,
                                                    }}
                                                >
                                                    <Chip
                                                        label={getTemplateTypeLabel(t.type)}
                                                        color='secondary'
                                                        size='small'
                                                    />
                                                    <IconButton
                                                        size='small'
                                                        onClick={e =>
                                                            onMenuOpen(e, { ...template, templateType: t.type })
                                                        }
                                                    >
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                </Box>
                                                <Typography variant='h6' noWrap gutterBottom>
                                                    {t.subject || 'No Subject'}
                                                </Typography>
                                                <Typography
                                                    variant='body2'
                                                    color='text.secondary'
                                                    sx={{
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                        mb: 2,
                                                    }}
                                                >
                                                    {t.body?.replace(/<[^>]*>/g, '').substring(0, 150)}...
                                                </Typography>
                                                <Stack direction='row' spacing={1}>
                                                    <Button
                                                        size='small'
                                                        variant='outlined'
                                                        startIcon={<VisibilityIcon />}
                                                        onClick={() =>
                                                            onPreview({ ...template, ...t, templateType: t.type })
                                                        }
                                                        fullWidth
                                                    >
                                                        Preview
                                                    </Button>
                                                    <Button
                                                        size='small'
                                                        variant='outlined'
                                                        startIcon={<EditIcon />}
                                                        onClick={() =>
                                                            onEdit({ ...template, ...t, templateType: t.type }, t.type)
                                                        }
                                                        fullWidth
                                                    >
                                                        Edit
                                                    </Button>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ));
                            })}
                        </Grid>
                    </Box>
                )}

                {fromEmails.length === 0 && fromTemplates.length === 0 && (
                    <Alert severity='info' sx={{ mt: 2 }}>
                        No templates found for this role. Create your first template above.
                    </Alert>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

